import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, XCircle, Mail, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { SignatureCanvas } from "@/components/shared/SignatureCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { api, unwrap } from "@/lib/api";

const DRAFT_NOTICE = (
  <div className="mt-4 rounded-xl bg-amber-50 border border-amber-300 px-4 py-3 text-xs text-amber-800" data-testid="bg-check-draft-notice">
    ⚠️ <b>DRAFT:</b> This document has not yet been reviewed by a licensed Massachusetts attorney.
    For specific legal questions, contact <a href="mailto:contact@evercleanhome.online" className="underline">contact@evercleanhome.online</a>.
  </div>
);

const DISCLOSURE_TEXT = `Ever Clean Home LLC ("the Company") may obtain information about you from a consumer reporting agency for employment purposes. Thus, you may be the subject of a "consumer report" and/or an "investigative consumer report" which may include information about your character, general reputation, personal characteristics, and/or mode of living, and which can involve personal interviews with sources such as your neighbors, friends, or associates. These reports may contain information regarding your criminal history, social security verification, motor vehicle records ("driving records"), verification of your education or employment history, or other background checks. You have the right, upon written request made within a reasonable time, to request whether a consumer report has been run about you, and disclosure of the nature and scope of any investigative consumer report. Please be advised that the nature and scope of the most common form of investigative consumer report obtained with regard to applicants for employment is an investigation into your education and/or employment history conducted by a consumer reporting agency selected by the Company or another outside organization. The scope of this notice and authorization is all-encompassing; however, allowing the Company to obtain from any outside organization all manner of consumer reports and investigative consumer reports now and, if you are hired, throughout the course of your employment to the extent permitted by law.`;

const AUTHORIZATION_TEXT = `By signing below, I authorize Ever Clean Home LLC to obtain a consumer report and/or investigative consumer report about me from a consumer reporting agency, as described in the disclosure I just reviewed. I understand that if I am hired, this authorization remains valid throughout my employment unless I revoke it in writing.`;

const ERROR_COPY = {
  token_expired: { title: "This link has expired", body: "Background check consent links expire after 7 days. Please contact us for a new one." },
  token_used: { title: "This step was already completed", body: "This background check consent has already been completed." },
  token_invalid: { title: "Invalid link", body: "This background check link is not valid." },
};

export default function BackgroundCheckWizardPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!token) { setErrorCode("token_invalid"); setLoading(false); return; }
    api.get("/bg-check/verify", { params: { token } })
      .then((r) => {
        const d = unwrap(r);
        setCandidate(d);
        if (d.completed) setStep(5);
        else if (d.address_count > 0 || d.has_personal_info) setStep(4);
        else if (d.authorization_accepted) setStep(3);
        else if (d.disclosure_accepted) setStep(2);
        else setStep(1);
      })
      .catch((err) => setErrorCode(err?.response?.data?.detail?.code || "token_invalid"))
      .finally(() => setLoading(false));
  }, [token]);

  const progressPct = Math.min(100, (step / 5) * 100);

  return (
    <div className="min-h-screen bg-[#0C1A1A] py-10 px-4" data-testid="bg-check-wizard-page">
      <SEO title="Background Check Consent — Ever Clean Home" noindex />
      <div className="w-full max-w-xl mx-auto">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-14 h-14 rounded-xl bg-white grid place-items-center p-2"><img src="/brand/logo-everclean-icon-192.png" alt="Ever Clean Home" className="w-full h-full object-contain" /></span>
          <span className="font-display font-extrabold text-xl text-white">Ever Clean Home</span>
        </Link>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {loading ? (
            <div className="grid place-items-center py-10" data-testid="bg-check-checking"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
          ) : errorCode ? (
            <ErrorState code={errorCode} />
          ) : (
            <>
              {step < 5 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-[#9CA3AF] mb-1.5">
                    <span>Step {step} of 4</span>
                    <span>{candidate?.first_name}</span>
                  </div>
                  <Progress value={progressPct} className="[&>div]:bg-[#0EA5A4]" data-testid="bg-check-progress" />
                </div>
              )}
              {step === 1 && <StepDisclosure token={token} onNext={() => setStep(2)} />}
              {step === 2 && <StepAuthorization token={token} onNext={() => setStep(3)} />}
              {step === 3 && <StepPersonalInfo token={token} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
              {step === 4 && <StepAddressHistory token={token} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
              {step === 5 && <StepDone />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorState({ code }) {
  const copy = ERROR_COPY[code] || ERROR_COPY.token_invalid;
  return (
    <div className="text-center py-4" data-testid="bg-check-error">
      <span className="w-12 h-12 rounded-full bg-rose-100 grid place-items-center mx-auto mb-4">
        <XCircle className="w-6 h-6 text-rose-600" />
      </span>
      <h1 className="font-display text-xl font-extrabold tracking-tighter">{copy.title}</h1>
      <p className="text-sm text-[#4B5563] mt-2">{copy.body}</p>
      <a href="mailto:contact@evercleanhome.online" className="mt-6 inline-block">
        <Button variant="outline" className="rounded-xl" data-testid="bg-check-contact-support"><Mail className="w-4 h-4 mr-2" /> Contact support</Button>
      </a>
    </div>
  );
}

function StepDisclosure({ token, onNext }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      await api.post("/bg-check/disclosure", { token, accepted: true });
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save — try again");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="bg-check-step-disclosure">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Background Check Disclosure</h1>
      <div className="mt-4 max-h-64 overflow-y-auto rounded-xl border border-[#E5E7EB] bg-[#FAFAF9] p-4 text-sm text-[#4B5563] leading-relaxed" data-testid="bg-check-disclosure-text">
        {DISCLOSURE_TEXT}
      </div>
      {DRAFT_NOTICE}
      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={setChecked} className="mt-0.5" data-testid="bg-check-disclosure-checkbox" />
        <span className="text-sm text-[#0C1A1A]">I have read and understand this disclosure</span>
      </label>
      <Button onClick={submit} disabled={!checked || saving} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold mt-6" data-testid="bg-check-disclosure-continue">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
      </Button>
    </div>
  );
}

function StepAuthorization({ token, onNext }) {
  const [fullName, setFullName] = useState("");
  const [signature, setSignature] = useState("");
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = new Date().toLocaleDateString();

  const submit = async () => {
    if (!fullName.trim() || !signature || !checked) {
      toast.error("Please sign, enter your full legal name and authorize the check");
      return;
    }
    setSaving(true);
    try {
      await api.post("/bg-check/authorization", { token, full_legal_name: fullName, signature_data_url: signature, accepted: true });
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save — try again");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="bg-check-step-authorization">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Authorization</h1>
      <p className="mt-4 text-sm text-[#4B5563] leading-relaxed" data-testid="bg-check-authorization-text">{AUTHORIZATION_TEXT}</p>
      {DRAFT_NOTICE}
      <div className="mt-5">
        <Label className="text-sm">Signature</Label>
        <div className="mt-1.5"><SignatureCanvas onChange={setSignature} testId="bg-check-signature-canvas" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <Label className="text-sm">Full legal name</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} className="rounded-xl mt-1" data-testid="bg-check-full-name-input" />
        </div>
        <div>
          <Label className="text-sm">Date</Label>
          <Input value={today} readOnly disabled className="rounded-xl mt-1 bg-[#F5F5F4]" data-testid="bg-check-date-input" />
        </div>
      </div>
      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={setChecked} className="mt-0.5" data-testid="bg-check-authorization-checkbox" />
        <span className="text-sm text-[#0C1A1A]">I authorize the background check</span>
      </label>
      <Button onClick={submit} disabled={saving} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold mt-6" data-testid="bg-check-authorization-continue">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
      </Button>
    </div>
  );
}

function StepPersonalInfo({ token, onBack, onNext }) {
  const [dob, setDob] = useState("");
  const [ssn, setSsn] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!dob || !/^\d{4}$/.test(ssn)) {
      toast.error("Please provide a valid date of birth and the last 4 digits of your SSN");
      return;
    }
    setSaving(true);
    try {
      await api.post("/bg-check/personal-info", { token, date_of_birth: dob, ssn_last_four: ssn });
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save — try again");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="bg-check-step-personal-info">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Personal Information</h1>
      <p className="mt-2 text-sm text-[#4B5563]">This information is required by our background check provider. It is encrypted and only used to verify your identity.</p>
      <div className="mt-5 space-y-4">
        <div>
          <Label className="text-sm">Date of birth</Label>
          <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="rounded-xl mt-1" data-testid="bg-check-dob-input" />
        </div>
        <div>
          <Label className="text-sm">Social Security Number (last 4 digits only)</Label>
          <Input type="password" inputMode="numeric" maxLength={4} value={ssn}
            onChange={(e) => setSsn(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="rounded-xl mt-1" data-testid="bg-check-ssn-input" />
          <p className="text-xs text-[#9CA3AF] mt-1">We only ask for the last 4 digits for identity verification. We never store your full SSN.</p>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} className="rounded-xl flex-1" data-testid="bg-check-personal-info-back">Back</Button>
        <Button onClick={submit} disabled={saving} className="rounded-xl flex-1 bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="bg-check-personal-info-continue">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
        </Button>
      </div>
    </div>
  );
}

const emptyAddress = () => ({ line1: "", line2: "", city: "", state: "", zip_code: "", from_date: "", to_date: "present" });

function StepAddressHistory({ token, onBack, onNext }) {
  const [addresses, setAddresses] = useState([emptyAddress()]);
  const [saving, setSaving] = useState(false);

  const update = (i, field, val) => setAddresses((arr) => arr.map((a, idx) => (idx === i ? { ...a, [field]: val } : a)));
  const addOne = () => setAddresses((arr) => [...arr, emptyAddress()]);
  const removeOne = (i) => setAddresses((arr) => arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/bg-check/address-history", { token, addresses });
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Please cover at least 7 years of address history");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="bg-check-step-address-history">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Address History</h1>
      <p className="mt-2 text-sm text-[#4B5563]">Please list all addresses where you have lived in the past 7 years, starting with the most recent.</p>
      <div className="mt-5 space-y-5">
        {addresses.map((a, i) => (
          <div key={i} className="rounded-xl border border-[#E5E7EB] p-4 space-y-3" data-testid={`bg-check-address-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">{i === 0 ? "Current address" : `Address ${i + 1}`}</span>
              {i > 0 && (
                <button type="button" onClick={() => removeOne(i)} className="text-rose-500" data-testid={`bg-check-address-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <Input placeholder="Address line 1" value={a.line1} onChange={(e) => update(i, "line1", e.target.value)} className="rounded-xl" data-testid={`bg-check-address-line1-${i}`} />
            <Input placeholder="Address line 2 (optional)" value={a.line2} onChange={(e) => update(i, "line2", e.target.value)} className="rounded-xl" data-testid={`bg-check-address-line2-${i}`} />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={a.city} onChange={(e) => update(i, "city", e.target.value)} className="rounded-xl" data-testid={`bg-check-address-city-${i}`} />
              <Input placeholder="State" value={a.state} onChange={(e) => update(i, "state", e.target.value)} className="rounded-xl" data-testid={`bg-check-address-state-${i}`} />
              <Input placeholder="ZIP" value={a.zip_code} onChange={(e) => update(i, "zip_code", e.target.value)} className="rounded-xl" data-testid={`bg-check-address-zip-${i}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="month" value={a.from_date} onChange={(e) => update(i, "from_date", e.target.value)} className="rounded-xl mt-1" data-testid={`bg-check-address-from-${i}`} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                {i === 0 ? (
                  <Input value="Present" readOnly disabled className="rounded-xl mt-1 bg-[#F5F5F4]" data-testid={`bg-check-address-to-${i}`} />
                ) : (
                  <Input type="month" value={a.to_date === "present" ? "" : a.to_date} onChange={(e) => update(i, "to_date", e.target.value)} className="rounded-xl mt-1" data-testid={`bg-check-address-to-${i}`} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" onClick={addOne} className="w-full rounded-xl mt-3" data-testid="bg-check-add-address">
        <Plus className="w-4 h-4 mr-2" /> Add another address
      </Button>
      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} className="rounded-xl flex-1" data-testid="bg-check-address-back">Back</Button>
        <Button onClick={submit} disabled={saving} className="rounded-xl flex-1 bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="bg-check-address-submit">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit"}
        </Button>
      </div>
    </div>
  );
}

function StepDone() {
  return (
    <div className="text-center py-4" data-testid="bg-check-step-done">
      <span className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
      </span>
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Thank you!</h1>
      <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">
        Your consent has been received. The background check will be completed within 3-7 business days.
        You will receive an email with the result.
      </p>
      <p className="mt-2 text-sm text-[#4B5563]">In the meantime, if you have any questions, reply to the email that brought you here.</p>
      <span className="inline-flex items-center gap-1.5 mt-5 text-xs text-[#9CA3AF]"><ShieldCheck className="w-3.5 h-3.5" /> Your data is encrypted and kept private</span>
    </div>
  );
}
